import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '../types';
import { api } from '../services/api';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isInitializing: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isLoading: false,
      isInitializing: true,
      error: null,

      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.login(email, password);
          set({ user: response.user, isLoading: false });
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Errore durante il login';
          set({ error: message, isLoading: false });
          throw err;
        }
      },

      register: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.register(email, password);
          set({ user: response.user, isLoading: false });
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Errore durante la registrazione';
          set({ error: message, isLoading: false });
          throw err;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await api.logout();
        } finally {
          set({ user: null, isLoading: false, error: null });
        }
      },

      checkAuth: async () => {
        set({ isInitializing: true });
        if (!api.isAuthenticated()) {
          set({ user: null, isInitializing: false });
          return;
        }

        try {
          const user = await api.get<User>('/auth/me');
          set({ user, isInitializing: false });
        } catch {
          set({ user: null, isInitializing: false });
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user }),
    }
  )
);
